import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MinusCircleOutlined } from '@ant-design/icons';
import { App, Alert, Button, Flex, Modal, Table, theme } from 'antd';

import ButtonWithRole from '~components/ButtonWithRole';
import { PointInTime, POINT_IN_TIME_COLUMN_WIDTH } from '~components/PointInTime';
import { CustomTooltip } from '~components/Tooltip';
import { UserRole } from '~features/PrerenderUser';
import { useGetGscSitemapsQuery, useImportGscSitemapMutation } from './api/gscApiSlice';

export type GscSitemapRow = {
  siteUrl: string;
  url: string;
  lastSubmitted?: string;
  importedSitemapId: number | null;
};

type GscUserError = {
  message: string;
};

type GscSitemapImportModalProps = {
  open: boolean;
  onClose: () => void;
};

const GSC_DISABLED_ROLES = [UserRole.BillingManager, UserRole.Guest];

// The same sitemap URL can be listed under several GSC properties (e.g. an
// sc-domain: and a URL-prefix property), so the URL alone is not a unique key.
const getRowKey = (row: GscSitemapRow) => `${row.siteUrl}|${row.url}`;

function GscSitemapImportModal({ open, onClose }: GscSitemapImportModalProps) {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const { data: gscSitemaps, isFetching } = useGetGscSitemapsQuery(undefined, { skip: !open });
  const [importGscSitemap] = useImportGscSitemapMutation();

  const sitemaps: GscSitemapRow[] = gscSitemaps?.data?.items ?? [];
  const userErrorMessages: string[] = Array.from(
    new Set((gscSitemaps?.data?.userErrors ?? []).map((userError: GscUserError) => userError.message))
  );

  const handleClose = () => {
    setSelectedKeys([]);
    onClose();
  };

  const handleImport = async () => {
    setIsImporting(true);
    const selectedRows = sitemaps.filter((row) => selectedKeys.includes(getRowKey(row)));
    const urls = Array.from(new Set(selectedRows.map((row) => row.url)));
    const results = await Promise.allSettled(urls.map((url) => importGscSitemap({ url }).unwrap()));
    const failedUrls = new Set(
      urls.filter((url, index) => {
        const result = results[index];
        // A 409 means the sitemap already exists in Prerender — the invalidated
        // query refetch will show it as imported, so only real failures stay selected.
        return result.status === 'rejected' && (result.reason as { status?: number })?.status !== 409;
      })
    );
    setSelectedKeys(selectedRows.filter((row) => failedUrls.has(row.url)).map(getRowKey));
    setIsImporting(false);
    const importedCount = urls.length - failedUrls.size;
    if (failedUrls.size > 0) {
      message.error(`Could not import: ${Array.from(failedUrls).join(', ')}`);
    }
    if (importedCount > 0) {
      message.success(`${importedCount} sitemap${importedCount > 1 ? 's' : ''} imported.`);
    }
  };

  return (
    <Modal
      title="Import sitemaps from Google Search Console"
      open={open}
      onCancel={handleClose}
      width={720}
      footer={
        <Flex justify="flex-end" gap={token.marginXS}>
          <Button onClick={handleClose}>Cancel</Button>
          <ButtonWithRole
            type="primary"
            disabledFor={GSC_DISABLED_ROLES}
            disabled={selectedKeys.length === 0}
            loading={isImporting}
            onClick={handleImport}
          >
            Import selected ({selectedKeys.length})
          </ButtonWithRole>
        </Flex>
      }
    >
      <Flex vertical gap={token.marginXS}>
        {userErrorMessages.map((userErrorMessage) => (
          <Alert key={userErrorMessage} type="error" showIcon closable description={userErrorMessage} />
        ))}
        <Table<GscSitemapRow>
          rowKey={getRowKey}
          dataSource={sitemaps}
          loading={isFetching}
          pagination={false}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys as string[]),
            getCheckboxProps: (record) => ({ disabled: !!record.importedSitemapId || isImporting }),
          }}
          columns={[
            { title: 'Site', dataIndex: 'siteUrl', key: 'siteUrl', width: 200, ellipsis: true },
            { title: 'Sitemap URL', dataIndex: 'url', key: 'url', ellipsis: true },
            {
              title: 'Last submitted',
              dataIndex: 'lastSubmitted',
              key: 'lastSubmitted',
              width: POINT_IN_TIME_COLUMN_WIDTH,
              render: (lastSubmitted?: string) => (lastSubmitted ? <PointInTime value={lastSubmitted} /> : null),
            },
            {
              title: 'Status',
              dataIndex: 'importedSitemapId',
              key: 'status',
              width: 90,
              align: 'center',
              render: (importedSitemapId: number | null) =>
                importedSitemapId ? (
                  <Link to={`/sitemaps/map/${importedSitemapId}`} style={{ color: token.colorPrimary }}>
                    View
                  </Link>
                ) : (
                  <CustomTooltip placement="top" message="Not imported">
                    <MinusCircleOutlined style={{ color: token.colorTextQuaternary }} />
                  </CustomTooltip>
                ),
            },
          ]}
        />
      </Flex>
    </Modal>
  );
}

export default GscSitemapImportModal;
